import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  TextInput, 
  toast,
  Select,
  ComboBox
} from '@etherisc/ui-kit';
import { PrimaryButton, NeutralButton } from '../../components/buttons';
import { FormCard } from '../../components/cards';
import { organizationService } from '../../services/organizationService';
import { usePermissions } from '../../hooks/usePermissions';
import { ApiError } from '../../utils/errorHandling';
import type { OrganizationType, Organization, UpdateOrganizationData } from '../../types/organization';
import { ORGANIZATION_TYPE_LABELS } from '../../types/organization';
import { validateTin } from '../../utils/tinValidation';
import { validatePhoneForSms } from '@myubuntu/phone-validation';
import {
  ChevronLeftIcon,
} from 'lucide-react';
import { COUNTRY_OPTIONS } from '../../config/countryList';

interface EditOrganizationFormData {
  name: string;
  code: string;
  type: OrganizationType;
  email?: string; // Organization email address
  tin?: string; // Tax Identification Number
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  isActive: boolean;
  // Contact person information
  contactPerson: {
    name: string;
    role: string;
    contactNumber: string;
    email: string;
  };
}

// Validation schema
const validationSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  code: z.string(), // Read-only field
  type: z.string(), // Read-only field
  email: z.string()
    .optional()
    .refine((val) => {
      if (!val || val === '') return true;
      return z.string().email().safeParse(val).success;
    }, 'Please enter a valid email address'),
  tin: z.string()
    .optional()
    .refine((val) => {
      if (!val || val === '') return true;
      const tinResult = validateTin(val.trim(), 'ZW');
      return tinResult.isValid;
    }, 'Please enter a valid Tax Identification Number'),
  phone: z.string()
    .min(1, 'Phone number is required')
    .refine((val) => {
      const phoneResult = validatePhoneForSms(val);
      return phoneResult.isValid;
    }, 'Please enter a valid international phone number (e.g., +263771234567)'),
  addressLine1: z.string().min(1, 'Address line 1 is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  isActive: z.boolean(),
  contactPerson: z.object({
    name: z.string().min(1, 'Contact person name is required'),
    role: z.string().min(1, 'Contact person role is required'),
    contactNumber: z.string()
      .min(1, 'Contact person phone number is required')
      .refine((val) => {
        const phoneResult = validatePhoneForSms(val);
        return phoneResult.isValid;
      }, 'Please enter a valid international phone number (e.g., +263771234567)'),
    email: z.string()
      .min(1, 'Contact person email is required')
      .email('Please enter a valid email address'),
  }),
});

export function EditOrganization() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const showSuccess = useCallback((message: string, description?: string) => {
    return toast.success(message, { description });
  }, []);
  const showError = useCallback((message: string, description?: string) => {
    return toast.error(message, { description });
  }, []);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { userPermissions } = usePermissions();
  
  // React Hook Form setup
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<EditOrganizationFormData>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      code: '',
      type: 'OTHER',
      email: '',
      tin: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      isActive: true,
      contactPerson: {
        name: '',
        role: '',
        contactNumber: '',
        email: '',
      },
    },
  });
  
  const formData = watch();
  
  // Check if user can edit organization type and status
  const canEditOrgTypeAndStatus = userPermissions?.includes('organizations:update') || false;

  // Load organization data
  useEffect(() => {
    const loadOrganization = async () => {
      if (!id) {
        showError('Invalid Organisation', 'Organisation ID is required');
        navigate('/admin/organizations');
        return;
      }

      try {
        setIsLoading(true);
        const organization: Organization = await organizationService.getOrganizationById(id);
        // Extract contact person from settings if available
        const contactPerson = organization.settings?.contact_person || {
          name: '',
          role: '',
          contactNumber: '',
          email: '',
        };

        reset({
          name: organization.name,
          code: organization.code,
          type: organization.type,
          email: (organization.settings as { email?: string })?.email || '',
          tin: (organization.settings as { tin?: string })?.tin || '',
          phone: organization.phone || '',
          addressLine1: organization.addressLine1 || '',
          addressLine2: organization.addressLine2 || '',
          city: organization.city || '',
          state: organization.stateProvince || '',
          postalCode: organization.postalCode || '',
          country: organization.country || '',
          isActive: organization.isActive,
          contactPerson: {
            name: contactPerson.name || '',
            role: contactPerson.role || '',
            contactNumber: contactPerson.contactNumber || '',
            email: contactPerson.email || '',
          },
        });
      } catch (error) {
        console.error('Error loading organization:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load organisation.';
        showError('Load Failed', errorMessage);
        navigate('/admin/organizations');
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganization();
  }, [id, showError, navigate]);


  const onSubmit = useCallback(async (data: EditOrganizationFormData) => {
    if (!id) return;
    
    setIsSubmitting(true);
    
    try {
      const updateData: UpdateOrganizationData = {
        name: data.name.trim(),
        addressLine1: data.addressLine1?.trim() || undefined,
        addressLine2: data.addressLine2?.trim() || undefined,
        city: data.city?.trim() || undefined,
        stateProvince: data.state?.trim() || undefined,
        postalCode: data.postalCode?.trim() || undefined,
        country: data.country?.trim() || undefined,
        phone: data.phone?.trim() || undefined,
        settings: {
          email: data.email?.trim() || undefined,
          tin: data.tin?.trim() || undefined,
          contact_person: {
            name: data.contactPerson.name.trim(),
            role: data.contactPerson.role.trim(),
            contactNumber: data.contactPerson.contactNumber.trim() || undefined,
            email: data.contactPerson.email.trim() || undefined,
          },
        },
      };
      
      // Only include type and isActive if user has permission to edit them
      if (canEditOrgTypeAndStatus) {
        updateData.type = data.type;
        updateData.isActive = data.isActive;
      }
      
      await organizationService.updateOrganization(id, updateData);
      
      showSuccess('Organisation Updated', `Organisation "${data.name}" has been updated successfully.`);
      navigate('/admin/organizations');
    } catch (error) {
      console.error('Error updating organization:', error);
      
      if (error instanceof ApiError) {
        showError('Update Failed', error.message);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to update organisation.';
        showError('Update Failed', errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [showSuccess, showError, navigate, id]);

  const handleCancel = useCallback(() => {
    navigate('/admin/organizations');
  }, [navigate]);

  // Organization type options
  const typeOptions = Object.entries(ORGANIZATION_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading organisation...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleCancel}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-md"
        >
          <ChevronLeftIcon size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Organisation</h1>
          <p className="text-gray-600 mt-1">
            Update organisation details and settings
          </p>
        </div>
      </div>

      {/* Form */}
      <FormCard title="Organisation Information">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TextInput
                label="Organisation Name *"
                placeholder="Enter organisation name"
                {...register('name')}
                error={errors.name?.message}
                required
              />
              <TextInput
                label="Organisation Code (read-only)"
                placeholder="e.g., ACME_CORP"
                {...register('code')}
                disabled={true}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label={canEditOrgTypeAndStatus ? "Organisation Type *" : "Organisation Type (read-only)"}
                options={typeOptions}
                value={formData.type}
                onValueChange={canEditOrgTypeAndStatus ? (value: string) => setValue('type', value as OrganizationType) : () => {}}
                placeholder="Select organisation type"
                disabled={!canEditOrgTypeAndStatus}
              />
              <Select
                label={canEditOrgTypeAndStatus ? "Status *" : "Status (read-only)"}
                options={statusOptions}
                value={formData.isActive?.toString()}
                onValueChange={canEditOrgTypeAndStatus ? (value: string) => setValue('isActive', value === 'true') : () => {}}
                placeholder="Select status"
                disabled={!canEditOrgTypeAndStatus}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TextInput
                label="Organisation Email"
                placeholder="Enter organisation email (e.g., info@example.com)"
                type="email"
                {...register('email')}
                error={errors.email?.message}
              />
              <TextInput
                label="Tax Identification Number (TIN)"
                placeholder="Enter TIN (e.g., 123-456-7890)"
                {...register('tin')}
                error={errors.tin?.message}
              />
            </div>

            {/* Contact Information & Address */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium mb-4">Contact Information & Address</h3>
              
              {/* Contact Person Details */}
              <div className="mb-6">
                <h4 className="text-md font-medium mb-3 text-gray-700">Contact Person</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextInput
                    label="Full Name *"
                    placeholder="Tendai Mukamuri"
                    {...register('contactPerson.name')}
                    error={errors.contactPerson?.name?.message}
                    required
                  />
                  <TextInput
                    label="Role/Title *"
                    placeholder="Operations Manager"
                    {...register('contactPerson.role')}
                    error={errors.contactPerson?.role?.message}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <TextInput
                    label="Contact Number *"
                    placeholder="+263771234567"
                    {...register('contactPerson.contactNumber')}
                    error={errors.contactPerson?.contactNumber?.message}
                    required
                  />
                  <TextInput
                    label="Email *"
                    type="email"
                    placeholder="tendai.mukamuri@company.co.zw"
                    {...register('contactPerson.email')}
                    error={errors.contactPerson?.email?.message}
                    required
                  />
                </div>
              </div>

              {/* Organization Contact & Address */}
              <div>
                <h4 className="text-md font-medium mb-3 text-gray-700">Organisation Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <TextInput
                    label="Phone *"
                    placeholder="+263771234567"
                    {...register('phone')}
                    error={errors.phone?.message}
                    required
                  />
                  <ComboBox
                    label="Country *"
                    options={COUNTRY_OPTIONS}
                    value={formData.country || ''}
                    onChange={(value: string) => setValue('country', value, { shouldValidate: true })}
                    placeholder="Select a country"
                    error={errors.country?.message}
                  />
                </div>
                <div className="space-y-4">
                  <TextInput
                    label="Address Line 1 *"
                    placeholder="123 Nelson Mandela Avenue"
                    {...register('addressLine1')}
                    error={errors.addressLine1?.message}
                    required
                  />
                  <TextInput
                    label="Address Line 2"
                    placeholder="Suite 5A"
                    {...register('addressLine2')}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                      label="City *"
                      placeholder="Harare"
                      {...register('city')}
                      error={errors.city?.message}
                      required
                    />
                    <TextInput
                      label="State/Province"
                      placeholder="Harare Province"
                      {...register('state')}
                    />
                  </div>
                  <TextInput
                    label="Postal Code"
                    placeholder="263"
                    {...register('postalCode')}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <NeutralButton
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </NeutralButton>
              <PrimaryButton
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingText="Updating..."
              >
                Update Organization
              </PrimaryButton>
            </div>
          </form>
      </FormCard>
    </div>
  );
} 